import React, { useState } from "react";
import { BetslipIcon, HamburgerIcon } from "@picklebet/ui";
import { useBetslipSelectionsCount } from "@/features/betslip/hooks/useBetslipSelectionsCount";
import { usePendingBetsCount } from "@/hooks/firestore/betting/useEntries";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { triggerHaptic } from "@/utilities/haptics";
import {
  useBottomNavItems,
  type BottomNavItem,
} from "../hooks/useBottomNavItems";
import { useDockActions, useDockState } from "../hooks/useDock";
import { useShellStore } from "../stores/shellStore";
import { ariaCurrent, isNavItemActive } from "../utils/isNavItemActive";
import type { NavLocation } from "../types";
import { AppShell } from "./AppShell";
import { Navbar } from "./Navbar";
import { NavLabel } from "./NavTree";

import * as styles from "./ShellBottomNav.module.scss";

// Kept mounted at zero, so the first selection or pending bet still bounces.
const BetslipBadge = () => {
  const selectionsCount = useBetslipSelectionsCount();
  const pendingBetsCount = usePendingBetsCount();
  const hasSelections = selectionsCount > 0;
  const count = hasSelections ? selectionsCount : pendingBetsCount;

  return (
    <span className={count > 0 ? styles.badge : styles.emptyBadge}>
      <Navbar.Badge tone={hasSelections ? "alert" : "neutral"} isBouncy>
        {count}
      </Navbar.Badge>
    </span>
  );
};

type CellProps = {
  item: BottomNavItem;
  location: NavLocation;
};

const Cell = ({ item, location }: CellProps) => {
  const menuOpen = useShellStore((state) => state.menuOpen);
  const toggleMenu = useShellStore((state) => state.toggleMenu);
  const { content: dockContent, open: dockOpen } = useDockState();
  const { toggleDock } = useDockActions();

  if (item.kind === "menu") {
    return (
      <button
        type="button"
        {...Navbar.itemProps({ active: menuOpen })}
        aria-expanded={menuOpen}
        onClick={toggleMenu}
      >
        <Navbar.Icon>
          <HamburgerIcon />
        </Navbar.Icon>
        <Navbar.Label>Menu</Navbar.Label>
      </button>
    );
  }

  if (item.kind === "betslip") {
    const isBetslipOpen = dockOpen && dockContent === "betslip";

    return (
      <button
        type="button"
        {...Navbar.itemProps({ active: isBetslipOpen })}
        aria-expanded={isBetslipOpen}
        onClick={() => toggleDock("betslip")}
      >
        <Navbar.Icon>
          <BetslipIcon />
        </Navbar.Icon>
        <Navbar.Label>Betslip</Navbar.Label>
        <BetslipBadge />
      </button>
    );
  }

  const { item: hub, href } = item;
  const isCovered = menuOpen || dockOpen;
  // A cell lights across its whole section, the Live tab included.
  const sectionOnly = { ...location, search: "" };

  return (
    <Link
      noStyles
      to={href}
      {...Navbar.itemProps({
        active: !isCovered && isNavItemActive(hub, sectionOnly),
      })}
      aria-current={ariaCurrent(hub, location)}
    >
      <Navbar.Icon>{hub.icon}</Navbar.Icon>
      <NavLabel item={hub} />
    </Link>
  );
};

export const ShellBottomNav = ({ location }: { location: NavLocation }) => {
  const { content: dockContent } = useDockState();
  const { items, isSettled, skeletonCells } = useBottomNavItems(
    dockContent !== "none",
  );
  const [hasSettled, setHasSettled] = useState(isSettled);

  if (isSettled && !hasSettled) {
    setHasSettled(true);
  }

  return (
    <AppShell.BottomNav className={styles.slot}>
      <AppShell.BottomNavPortal className={styles.portal} />
      <Navbar.Root
        orientation="horizontal"
        tone="accent"
        isFullWidth
        aria-label="Quick links"
        className={styles.pill}
        onClick={() => triggerHaptic.click()}
      >
        {hasSettled
          ? items.map((item) => (
              <Navbar.Group
                key={item.kind === "hub" ? item.item.id : item.kind}
              >
                <Cell item={item} location={location} />
              </Navbar.Group>
            ))
          : Array.from({ length: skeletonCells }, (_, index) => (
              <Navbar.SkeletonRow key={index} />
            ))}
      </Navbar.Root>
    </AppShell.BottomNav>
  );
};
